import { DictionaryService } from 'src/app/data/dictionary.service';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { FormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { Moment } from 'moment';
// Material's configured date adapter and the existing range/calendar API use Moment values.
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, first, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { setFormControlValidators } from 'src/app/common/utils/set-form-control-validators';
import { TimezoneHelper } from 'src/app/common/utils/timezone.helper';
import {
  AbsenceFilesRequest,
  AbsenceFileUploadRequest,
  CreateOrUpdateAbsenceRequest,
} from 'src/app/contracts/requests/create-or-update-absence-request';
import { AbsenceService } from 'src/app/data/absence.service';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { AbsenceDayLimitPoolDto, AbsenceLimitHistoryDto, AbsenceSubtypeDto } from 'src/app/models/dtos/absence-subtype-dto';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { maxDateRangeValidator } from 'src/app/shared/validators/max-date-range.validator';
import { maxTimeRangeValidator } from 'src/app/shared/validators/max-time-range.validator';
import { AbsenceFormFamilyMembersPickerComponent } from '../absence-form-family-members-picker/absence-form-family-members-picker.component';
import { HolidayDto } from 'src/app/models/dtos/holiday-dto';
import { WeekDay } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MomentMonthEnum } from 'src/app/shared/enums/MomentMonthIndexEnum';
import { AbsenceSubtype } from 'src/app/models/enums/absence-subtype';
import { WorkerService } from 'src/app/data/worker.service';
import { AbsenceType } from 'src/app/models/enums/absence-type';
import { AuthService } from '../../core/authentication/auth.service';
import { Permission } from '../../common/enums';
import { AbsenceLimitCalcTypeEnum } from 'src/app/models/enums/absence-limit-calc-type-enum';
import { SubstituteWorekerDto } from 'src/app/models/dtos/substitute-worker-dto';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { ApprovalStep, FinalDecisionMeta, StepState } from './approval-progress/approval-progress.component';
import { AbsenceApprovalDto, AbsenceApprovalProgressStepDto, StepStatusEnum } from 'src/app/models/dtos/absence-approval-progress-step-dto';
import { WorkingTimePeriodDto, WorkingTimePeriodsDto } from 'src/app/models/dtos/absences/working-time-periods-dto';
import { findPeriod, periodContains, periodContainsRange, toDateKey } from '../services/working-time-periods';
import { scaleDayLimit } from '../services/absence-limit-scaling';

interface AbsenceFormData {
  WorkerId: number;
  Absence: AbsenceDto;
  WorkerAgreementId: number | null;
  EmploymentTypeId: number | null;
  HasNextYearTimeLogging: boolean;
  /** Periods already loaded by the caller for the same agreement; fetched by the form when missing. */
  WorkingTimePeriods?: WorkingTimePeriodsDto | null;
  WorkingTimePeriod?: WorkingTimePeriodDto | null;
}

@Component({
  selector: 'app-absence-form',
  templateUrl: './absence-form.component.html',
  styleUrls: ['./absence-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AbsenceModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private changeDetector = inject(ChangeDetectorRef);
  private spinner = inject(NgxSpinnerService);
  private dialog = inject(MatDialog);
  private dialogRef = inject<MatDialogRef<AbsenceModalComponent>>(MatDialogRef);
  private absenceService = inject(AbsenceService);
  private workerAgreementService = inject(WorkerAgreementService);
  private workerService = inject(WorkerService);
  private dictionaryService = inject(DictionaryService);
  private snackbar = inject(SnackBarService);
  private renderer = inject(Renderer2);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);
  data = inject<AbsenceFormData>(MAT_DIALOG_DATA);

  absenceForm!: ReturnType<AbsenceModalComponent['createAbsenceFormGroup']>;
  absenceSubtype!: AbsenceSubtypeDto;

  public dateLimits!: AbsenceLimitHistoryDto[];
  protected readonly timeBetweenInput = 300;
  protected readonly minimumInputLetters = 2;
  private absenceTypesSubject = new BehaviorSubject<number>(null);
  absenceTypes$: Observable<DictionaryItem[]> = this.absenceTypesSubject.pipe(
    switchMap((workerAgreementId) =>
      !!workerAgreementId ? this.workerAgreementService.getAbsenceTypesByWorkerAgreementId(workerAgreementId) : of([]),
    ),
  );

  substituteWorkers$!: Observable<SubstituteWorekerDto[]>;

  absenceLeaveCareKinshipDegrees$!: Observable<DictionaryItem[]>;
  absenceSubtypes$: Observable<AbsenceSubtypeDto[]> = of([]);

  absenceDateRange$ = new BehaviorSubject<number>(0);
  absenceTimeRange$ = new BehaviorSubject<number>(0);
  requiredFileTypes$ = new BehaviorSubject<DictionaryItem[]>([]);

  isWorkerFamilyMemberRequired$ = new BehaviorSubject<boolean>(false);
  mayBeHourly$ = new BehaviorSubject<boolean>(false);
  isPaid$ = new BehaviorSubject<boolean>(false);
  overdueLimitLeft$ = new BehaviorSubject<number>(0);
  hasNextYearHoliday: boolean = false;
  areWeekendsDaysOff$ = new BehaviorSubject<boolean>(false);
  ignoreCalendarValidation$ = new BehaviorSubject<boolean>(false);
  holidayCalendar$ = new BehaviorSubject<HolidayDto[]>([]);
  absenceLimitCalcType$ = new BehaviorSubject<AbsenceLimitCalcTypeEnum>(undefined);
  daysOff$ = this.holidayCalendar$.pipe(map((dates) => dates.map((date) => date.Date)));

  canEditAbsenceForm$ = new BehaviorSubject<boolean>(true);
  substitutionWorkerEdit$ = new BehaviorSubject<boolean>(false);

  private listeners: (() => void)[] = [];
  private absenceFilesToUpload: AbsenceFileUploadRequest[] = [];
  private familyMemberFilesMap: Map<number, AbsenceFileUploadRequest[]> = new Map<number, AbsenceFileUploadRequest[]>();
  private shouldResetEndDate: boolean = true;
  private isApprovalRequired: boolean = false;
  private hasBeforePreviousMonthStartLimit: boolean = false;
  private workingTimePeriods: WorkingTimePeriodsDto | null = null;
  workingTimePeriodsLoading = true;
  workingTimePeriodsError = false;
  workingTimePeriod: WorkingTimePeriodDto | null = null;

  private readonly unsubscribe$ = new Subject<void>();
  private readonly defaultRangeWhenHourlyAbsence = 1;
  @ViewChild(AbsenceFormFamilyMembersPickerComponent) familyMembersPicker!: AbsenceFormFamilyMembersPickerComponent;

  constructor() {

  }

  get startDate() {
    return this.absenceForm.get('startDate');
  }
  get endDate() {
    return this.absenceForm.get('endDate');
  }
  get startTime() {
    return this.absenceForm.get('startTime');
  }
  get endTime() {
    return this.absenceForm.get('endTime');
  }
  get absenceTypeId() {
    return this.absenceForm.get('absenceTypeId');
  }
  get absenceSubtypeIdControl() {
    return this.absenceForm.get('absenceSubtypeId');
  }
  get personToCareFullName() {
    return this.absenceForm.get('personToCareFullName');
  }
  get personToCareAddress() {
    return this.absenceForm.get('personToCareAddress');
  }
  get reasonToCare() {
    return this.absenceForm.get('reasonToCare');
  }
  get absenceLeaveCareKinshipDegreeId() {
    return this.absenceForm.get('absenceLeaveCareKinshipDegreeId');
  }
  get substituteWorkerControl() {
    return this.absenceForm.get('substituteWorker');
  }
  private get selectedSubstitute(): SubstituteWorekerDto | null {
    const value = this.substituteWorkerControl.value;
    return typeof value === 'string' ? null : value;
  }
  get approvingButtonsVisible() {
    return this.data?.Absence?.ApprovalPermissions?.some((permissionDto) => this.authService.hasPermission(permissionDto.Permission as Permission)) || false;
  }
  get absenceStatusId(): AbsenceStatus {
    return this.data?.Absence?.StatusId ?? AbsenceStatus.Draft;
  }
  get workingTimePeriodMissing(): boolean {
    return !!this.workingTimePeriods?.IsScaled && (this.workingTimePeriods.Periods.length === 0
      || (!!this.startDate.value?.isValid() && !this.workingTimePeriod));
  }

  finalDecisionMeta!: FinalDecisionMeta;
  intermediateSteps!: ApprovalStep[];

  async ngOnInit(): Promise<void> {
    this.absenceTypesSubject.next(this.data.WorkerAgreementId);
    this.buildAbsenceFormGroup(this.data.Absence);
    this.handleAbsenceRangeChange();
    this.handleAbsenceTypeChange();
    this.handleAbsenceSubtypeChange();
    this.handleMayBeHourlyChange();
    this.handleSubstituteWorkerChange();

    this.absenceService.getAbsenceSettingsByWorkerAgreementId(this.data.WorkerAgreementId).pipe(takeUntil(this.unsubscribe$)).subscribe((settings) => {
      this.hasBeforePreviousMonthStartLimit = settings.HasBeforePreviousMonthStartLimit;
      this.changeDetector.markForCheck();
    });

    if (this.data.WorkingTimePeriods) {
      this.workingTimePeriods = this.data.WorkingTimePeriods;
      this.workingTimePeriodsLoading = false;
    } else if (this.data.WorkerAgreementId) {
      this.absenceService
        .getWorkingTimePeriods(this.data.WorkerAgreementId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (periods) => {
            this.workingTimePeriods = periods;
            this.workingTimePeriodsLoading = false;
            this.refreshDateLimits();
            this.changeDetector.markForCheck();
          },
          error: () => {
            this.workingTimePeriodsLoading = false;
            this.workingTimePeriodsError = true;
            this.revalidateDateControls();
            this.changeDetector.markForCheck();
          },
        });
    } else {
      this.workingTimePeriodsLoading = false;
      this.workingTimePeriodsError = true;
    }

    if (this.data?.Absence) {
      this.finalDecisionMeta = {
        acceptedOn: this.data.Absence.AcceptedOn,
        acceptedBy: this.data.Absence.AcceptedBy,
        rejectedOn: this.data.Absence.RejectedOn,
        rejectedBy: this.data.Absence.RejectedBy,
        statusName: this.data.Absence.Status
      }
    }
  }

  ngAfterViewInit(): void {
    this.patchFormGroupValue();
    this.onStartDateChange();
    this.getHolidays(this.data.WorkerAgreementId);
  }

  ngOnDestroy(): void {
    this.listeners.forEach((removeListener) => removeListener());
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  isOverdueLimit = (): boolean => this.overdueLimitLeft$.getValue() > 0;

  isTemporaryAgreement = (): boolean => this.data.EmploymentTypeId == EmploymentType.TemporaryEmploymentAgreement;

  isSendToApprovalButtonVisible = () => this.isApprovalRequired && (!this.data.Absence || this.data.Absence.StatusId === AbsenceStatus.Draft);

  isConfirmAbsenceButtonVisible = () => !this.isApprovalRequired && (!this.data.Absence || this.data.Absence.StatusId === AbsenceStatus.Draft);

  isSubmitDraftButtonVisible = () => !this.data.Absence || this.data.Absence.StatusId === AbsenceStatus.Draft;

  isBackToDraftButtonVisible = () => this.data.Absence?.StatusId === AbsenceStatus.PendingApproval;

  isAcceptAbsenceButtonVisible = () => this.data.Absence?.StatusId === AbsenceStatus.PendingApproval && !this.isSelfAbsence();

  isRejectAbsenceButtonVisible = () => this.data.Absence?.StatusId === AbsenceStatus.PendingApproval && !this.isSelfAbsence();

  isFamilyMemberCare = () => this.absenceSubtype?.Id === AbsenceSubtype.FamilyMemberCare;

  isHouseholdMemberCare = () => this.absenceSubtype?.Id === AbsenceSubtype.HouseholdMemberCare;

  isSelfAbsence = (): boolean => this.data.Absence.AuthServerUserId === this.authService.authServerUserId;

  isCancelAbsenceButtonVisible = () =>
    this.data.Absence?.StatusId === AbsenceStatus.Accepted && this.data.Absence?.AbsenceTypeId !== AbsenceType.SickLeave;

  close = () => this.dialogRef.close();

  getHolidays(agreementId: number) {
    this.workerAgreementService
      .getHolidaysByWorkerAgreementId(agreementId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((response) => this.areWeekendsDaysOff$.next(response?.AreWeekendsDaysOff || false)),
        tap((response) => {
          this.holidayCalendar$.next(response?.DaysOff || []);
          this.changeDetector.markForCheck();
        }),
      )
      .subscribe();
  }

  dateFilter = (d: Moment) => {
    if (!d?.isValid() || !this.absenceSubtype) {
      return false;
    }
    return (
      (!this.isBeforeFirstDayOfPreviousMonth(d) || this.isAllowToSkipPreviousMonthAbsenceLimit()) &&
      this.absenceLimitFilter(d) &&
      this.isWorkingDay(d) &&
      this.isWithinWorkingTimePeriod(d)
    );
  };

  private isWithinWorkingTimePeriod = (d: Moment): boolean => {
    if (this.workingTimePeriodsLoading || this.workingTimePeriodsError) {
      return false;
    }
    if (!this.workingTimePeriods?.IsScaled) {
      return true;
    }

    // Picking the end date: same period as the start date; picking the start date: period selected on the profile.
    const period = this.startDate.value && !this.endDate.value && !this.mayBeHourly$.getValue()
      ? findPeriod(this.workingTimePeriods.Periods, this.startDate.value.toDate())
      : this.data.WorkingTimePeriod ?? null;

    return period
      ? periodContains(period, toDateKey(d.toDate()))
      : !!findPeriod(this.workingTimePeriods.Periods, d.toDate());
  };

  private readonly workingTimePeriodValidator: ValidatorFn = () => {
    if (this.workingTimePeriodsLoading || this.workingTimePeriodsError) {
      return { workingTimePeriodsUnavailable: true };
    }
    if (!this.workingTimePeriods?.IsScaled) {
      return null;
    }

    const start = this.startDate.value;
    const end = this.endDate.value;
    if (!start?.isValid() || !end?.isValid()) {
      return null;
    }
    const period = findPeriod(this.workingTimePeriods.Periods, start.toDate());
    const selectedPeriod = this.data.WorkingTimePeriod;
    return period && periodContainsRange(period, start.toDate(), end.toDate())
      && (!selectedPeriod || periodContainsRange(selectedPeriod, start.toDate(), end.toDate()))
      ? null
      : { AbsenceSpansMultipleWorkingTimePeriods: true };
  };

  private absenceLimitFilter = (d: Moment) => {
    let result = false;

    if (this.absenceSubtype?.IsPaid && !this.hasAnnualLimits() && !this.hasHistoryLimits()) {
      return result;
    }

    if (this.isTemporaryAgreement() && this.hasHistoryLimits() && d.toDate() < new Date(Date.now()).resetTime()) {
      result = this.isDateAfterMonthAgo(d) && this.canBeTakenInHistory(d);
    } else if (this.isOverdueLimit() || this.isTemporaryAgreement()) {
      result = this.isDateAfterMonthAgo(d) && (!this.startDate.value || this.startDate.value.year() === d.year() || !!this.endDate.value);
    } else {
      result =
        (d.year() === moment().year() ||
          (d.year() === moment().year() - 1 && d.month() === MomentMonthEnum.December) ||
          (this.data.HasNextYearTimeLogging && this.absenceSubtype.AnnualAbsenceLimits.find((x) => x.Year == d.year()))?.AbsenceLimitLeft > 0) &&
        (!this.startDate.value || moment(this.startDate.value).year() === d.year());
    }

    return result;
  };

  private isWorkingDay = (d: Moment) => {
    if (this.ignoreCalendarValidation$.getValue()) {
      return true;
    }

    return d.toDate().isWorkingDay(
      this.areWeekendsDaysOff$.getValue(),
      this.holidayCalendar$.getValue().map((d) => d.Date),
      this.absenceLimitCalcType$.getValue(),
    );
  };

  private hasHistoryLimits = () => !!this.dateLimits && this.dateLimits.length > 0;

  private hasAnnualLimits = () => !!this.absenceSubtype.AnnualAbsenceLimits && this.absenceSubtype.AnnualAbsenceLimits.length > 0;

  private isDateAfterMonthAgo = (d: Moment) => d.isSameOrAfter(moment().startOf('year').subtract(1, 'month'));

  private canBeTakenInHistory(d: moment.Moment) {
    const dayLimit = this.dateLimits?.find((dateLimit) => new Date(dateLimit.Date).compareOnlyDates(d.toDate()));
    return dayLimit?.OverdueLimitLeft > 0 || dayLimit?.AbsenceLimitLeft > 0;
  }

  private getRawHistoryLimit = (value: moment.Moment) => {
    const dayLimit = this.dateLimits?.find((dateLimit) => new Date(dateLimit.Date).compareOnlyDates(value.toDate()));
    const leftLimit = dayLimit?.AbsenceLimitLeft ?? 0;
    const overdueLeftLimit = dayLimit?.OverdueLimitLeft ?? 0;
    return leftLimit + overdueLeftLimit;
  };

  private getHistoryLimit = (value: moment.Moment) => {
    return this.getScaledDayLimit(this.getRawHistoryLimit(value), value.toDate()) ?? 0;
  };

  sendToApproval() {
    if (this.absenceForm.invalid || !this.areFilesToUploadValid()) {
      this.absenceForm.markAllAsTouched();
      this.familyMembersPicker?.familyMembersForm?.markAllAsTouched();
      return;
    }

    const onConfirm = (absenceId) => {
      this.spinner.show();
      const action$ = absenceId
        ? this.absenceService.updateAbsence(absenceId, this.request())
        : this.absenceService.createDraftAbsence(this.request());

      return action$.pipe(
        first(),
        switchMap((updatedAbsenceId) => this.absenceService.sendToApproval(updatedAbsenceId).pipe(first())),
        tap((_) => this.closeDialogSuccessfully(Messages.SuccessfullySentToApprovalAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    if (this.isBeforeFirstDayOfPreviousMonth(this.startDate?.value) && this.hasBeforePreviousMonthStartLimit) {
      this.dialog
        .open(ConfirmDialogComponent, { data: { message: Messages.BeforeFirstDayLastMonthWarning } })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm(this.data.Absence?.Id) : EMPTY)),
        )
        .subscribe();
    } else {
      onConfirm(this.data.Absence?.Id).subscribe();
    }
  }

  confirmAbsence() {
    if (this.absenceForm.invalid || !this.areFilesToUploadValid()) {
      this.absenceForm.markAllAsTouched();
      this.familyMembersPicker?.familyMembersForm?.markAllAsTouched();
      return;
    }

    this.spinner.show();
    const action$ = this.data.Absence?.Id
      ? this.absenceService.updateAbsence(this.data.Absence.Id, this.request())
      : this.absenceService.createDraftAbsence(this.request());

    action$
      .pipe(
        first(),
        switchMap((absenceId) => this.absenceService.confirmAbsence(absenceId).pipe(first())),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(Messages.SuccessfullyConfirmedAbsence));
  }

  submitDraft() {
    if (this.absenceForm.invalid || !this.areFilesToUploadValid()) {
      this.absenceForm.markAllAsTouched();
      this.familyMembersPicker?.familyMembersForm?.markAllAsTouched();
      return;
    }

    const action$ = this.data.Absence?.Id
      ? this.absenceService.updateAbsence(this.data.Absence.Id, this.request())
      : this.absenceService.createDraftAbsence(this.request());
    const message = !this.data.Absence?.Id ? Messages.SuccessfullyCreatedAbsence : Messages.SuccessfullyUpdatedAbsence;
    this.spinner.show();
    action$
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(message));
  }

  backToDraftAbsence() {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.backToDraftAbsence(absenceId).pipe(
        tap((_) => this.closeDialogSuccessfully(Messages.SuccessfullyBackToDraftAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmBackToDraftAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(this.data.Absence.Id) : EMPTY)),
      )
      .subscribe();
  }

  acceptAbsence() {
    const onConfirm = () => {
      const currentSubstituteWorkerId = this.selectedSubstitute?.WorkerId ?? null;
      const originalSubstituteWorkerId = this.data.Absence.SubstituteWorker?.WorkerId ?? null;
      const substituteChanged = currentSubstituteWorkerId !== originalSubstituteWorkerId;

      if (this.absenceSubtype?.RequireLeaveSubstitution && this.substituteWorkerControl.invalid) {
        this.substituteWorkerControl.markAsTouched();
        return EMPTY;
      }

      this.spinner.show();
      const action$ = substituteChanged && (this.canEditAbsenceForm$.getValue() || this.substitutionWorkerEdit$.getValue())
        ? this.absenceService.updateAbsenceSubstituteWorker(this.data.Absence.Id, currentSubstituteWorkerId)
        : of(null);

      return action$
        .pipe(
          first(),
          switchMap((_) => this.absenceService.acceptAbsence(this.data.Absence.Id).pipe(first())),
          tap((_) => this.closeDialogSuccessfully(Messages.SuccesfullyAcceptedAbsence)),
          finalize(() => this.spinner.hide()),
        )
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmAcceptingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
      )
      .subscribe();
  }

  rejectAbsence() {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.rejectAbsence(this.data.Absence.Id).pipe(
        first(),
        tap((_) => this.closeDialogSuccessfully(Messages.SuccesfullyRejectedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmRejectingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
      )
      .subscribe();
  }

  async cancelAbsence() {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.cancelAbsence(this.data.Absence.Id).pipe(
        first(),
        tap((_) => this.closeDialogSuccessfully(Messages.SuccessfullyCanceledAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmCancellingAbsenceMessage } })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  isSaveAbsenceFilesButtonVisible(): boolean {
    return (
      this.data.Absence?.StatusId !== AbsenceStatus.Draft &&
      this.requiredFileTypesLength() > 0 &&
      this.data.Absence?.Files.filter((f) => !!f.Name).length < this.requiredFileTypesLength()
    );
  }

  isSaveAbsenceFilesButtonDisable(): boolean {
    return !this.absenceFilesToUpload?.length;
  }

  requiredFileTypesLength(): number {
    return this.requiredFileTypes$.getValue().length;
  }

  saveAbsenceFiles() {
    if (this.absenceForm.invalid || !this.areFilesToUploadValid()) {
      this.absenceForm.markAllAsTouched();
      this.familyMembersPicker?.familyMembersForm?.markAllAsTouched();
      return;
    }

    this.spinner.show();
    this.absenceService
      .saveAbsenceFiles(this.data.Absence.Id, this.fileRequest())
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(Messages.SuccessfullyUpdatedAbsence));
  }

  afterFileUpload = (files: AbsenceFileUploadRequest[]) => {
    this.absenceFilesToUpload = files;
  };

  onFamilyMembersChange = (familyMembersFilesMap: Map<number, AbsenceFileUploadRequest[]>) => {
    this.familyMemberFilesMap = familyMembersFilesMap;
  };

  calendarClosed() {
    this.listeners.forEach((v) => v());
  }

  calendarOpened(event) {
    setTimeout(() => {
      this.listeners.forEach((v) => v());
      this.listeners = [];

      let buttons = document.querySelectorAll('mat-calendar .mat-calendar-body-cell, mat-calendar button, mat-calendar .mat-icon-button');

      buttons.forEach((btn) => {
        let x = this.renderer.listen(btn, 'click', () => {
          setTimeout(() => {
            this.calendarOpened(event);
          });
        });

        this.listeners.push(x);
      });
      this.updateDayStyles();
    });
  }

  updateDayStyles() {
    let elements = document.querySelectorAll('.with-tooltip .mat-calendar-content');
    let calendarCells: NodeListOf<HTMLElement> | HTMLElement[] = elements.length > 0 ? elements[0].querySelectorAll<HTMLElement>('.mat-calendar-body-cell') : [];
    const namedDates = this.holidayCalendar$.getValue();

    calendarCells.forEach((calendarCell) => {
      let found = false;
      let calendarDate = moment(calendarCell.getAttribute('aria-label'));

      if (this.isBeforeFirstDayOfPreviousMonth(calendarDate) && !this.isAllowToSkipPreviousMonthAbsenceLimit()) {
        found = true;
        this.addTooltip(calendarCell, this.translateService.instant('BlockedDateBeforeFirstDayOfPreviousMonth'));
      }

      namedDates.forEach((namedDate) => {
        let date = moment(namedDate.Date);

        if (calendarDate.isSame(date, 'day')) {
          found = true;
          this.addTooltip(calendarCell, namedDate.Name);
        }
      });

      if (!found && this.areWeekendsDaysOff$.getValue() && (calendarDate.day() === WeekDay.Saturday || calendarDate.day() === WeekDay.Sunday)) {
        found = true;
        const dayName = calendarDate.locale(this.translateService.currentLang).format('dddd').toLocaleLowerCase();
        this.addTooltip(calendarCell, dayName[0].toUpperCase() + dayName.slice(1));
      }

      if (!found && !this.absenceLimitFilter(calendarDate)) {
        found = true;
        this.addTooltip(calendarCell, this.translateService.instant('OutOfAbcenceLimitDay'));
      }

      if (!found) {
        this.removeTooltip(calendarCell);
      }
    });
  }

  private handleSubstituteWorkerChange(): void {
    this.substituteWorkers$ = combineLatest([
      this.substituteWorkerControl.valueChanges.pipe(startWith(this.substituteWorkerControl.value)),
      this.startDate.valueChanges.pipe(startWith(this.startDate.value)),
      this.endDate.valueChanges.pipe(startWith(this.endDate.value)),
    ]).pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      filter(() => !!this.absenceSubtype?.RequireLeaveSubstitution),
      switchMap(([name, startDate, endDate]) => {
        if (name && typeof name === 'string') {
          if (startDate && endDate) {
            if (name.length > this.minimumInputLetters) {
              return this.absenceService.getSubstituteWorkers(this.data.WorkerAgreementId, { Name: name, DateFrom: startDate.toDate(), DateTo: endDate.toDate() });
            }
          } else {
            this.startDate.markAsTouched();
            this.endDate.markAsTouched();
          }
        } else if (startDate && endDate && typeof name !== 'string' && name?.FullName) {
          return this.absenceService
            .getSubstituteWorkers(this.data.WorkerAgreementId, { Name: name.FullName, DateFrom: startDate.toDate(), DateTo: endDate.toDate() })
            .pipe(
              tap((res) => {
                if (!res?.length) {
                  this.substituteWorkerControl.setValue(null, { emitEvent: false });
                  this.substituteWorkerControl.markAsTouched();
                }
              }),
            );
        }

        return of(null);
      }),
    );
  }

  private addTooltip(calendarCell: HTMLElement, tooltip: string) {
    calendarCell.classList.add('calendar-tooltip');

    let tooltipSpan = calendarCell.querySelector('.tooltiptext');

    if (!tooltipSpan) {
      tooltipSpan = document.createElement('span');
      tooltipSpan.classList.add('tooltiptext');
      calendarCell.appendChild(tooltipSpan);
    }

    tooltipSpan.textContent = (tooltip ?? '').replace(/<br\s*\/?\s*>/gi, '\n');
  }

  private removeTooltip(calendarCell: HTMLElement) {
    calendarCell.classList.remove('calendar-tooltip');

    const tooltipSpan = calendarCell.querySelector('.tooltiptext');
    if (tooltipSpan) {
      calendarCell.removeChild(tooltipSpan);
    }
  }

  private closeDialogSuccessfully = (message: string) => {
    this.snackbar.openSuccessSnackBar(message);
    this.dialogRef.close(true);
  };

  private request = (): CreateOrUpdateAbsenceRequest => ({
    AbsenceTypeId: this.absenceForm.get('absenceTypeId')?.value,
    AbsenceSubtypeId: this.absenceForm.get('absenceSubtypeId')?.value,
    StartDate: this.getDateTimeValue('startDate', 'startTime'),
    EndDate: this.getDateTimeValue('endDate', 'endTime'),
    Comment: this.absenceForm.get('absenceReason')?.value,
    WorkerAgreementId: this.data.WorkerAgreementId,
    IsHourly: this.mayBeHourly$.getValue(),
    PersonToCareFullName: this.absenceForm.get('personToCareFullName')?.value,
    PersonToCareAddress: this.absenceForm.get('personToCareAddress')?.value,
    ReasonToCare: this.absenceForm.get('reasonToCare')?.value,
    AbsenceLeaveCareKinshipDegreeId: this.absenceForm.get('absenceLeaveCareKinshipDegreeId')?.value,
    Files: this.absenceFilesToUpload,
    FamilyMembersFiles: Array.from(this.familyMemberFilesMap.keys()).map((workerFamilyMemberId) => ({
      WorkerFamilyMemberId: workerFamilyMemberId,
      Files: this.familyMemberFilesMap.get(workerFamilyMemberId),
    })),
    SubstituteWorkerId: this.selectedSubstitute?.WorkerId,
  });

  private fileRequest = (): AbsenceFilesRequest => ({
    AbsenceTypeId: this.absenceForm.get('absenceTypeId')?.value,
    Files: this.absenceFilesToUpload,
    FamilyMembersFiles: Array.from(this.familyMemberFilesMap.keys()).map((workerFamilyMemberId) => ({
      WorkerFamilyMemberId: workerFamilyMemberId,
      Files: this.familyMemberFilesMap.get(workerFamilyMemberId),
    })),
  });

  private getDateTimeValue = (dateControlName: string, timeControlName: string) => {
    const date = this.absenceForm.get(dateControlName)?.value;
    const time: string | null = this.absenceForm.get(timeControlName)?.value;
    return TimezoneHelper.createDateTimeInLocalWithTimezone(date, time);
  };

  private createAbsenceFormGroup(absence: AbsenceDto) {
    return this.formBuilder.group({
      absenceReason: [absence?.AbsenceReason, Validators.maxLength(250)],
      absenceTypeId: [absence?.AbsenceTypeId, Validators.required],
      absenceSubtypeId: [absence?.AbsenceSubtypeId, Validators.required],
      startDate: [absence?.StartDate ? moment(absence.StartDate) : null, Validators.required],
      endDate: [absence?.EndDate ? moment(absence.EndDate) : null, Validators.required],
      startTime: this.formBuilder.control<string | null>(null),
      endTime: this.formBuilder.control<string | null>(null),
      personToCareFullName: [absence?.PersonToCareFullName],
      personToCareAddress: [absence?.PersonToCareAddress],
      reasonToCare: [absence?.ReasonToCare],
      absenceLeaveCareKinshipDegreeId: [absence?.AbsenceLeaveCareKinshipDegreeId],
      substituteWorker: this.formBuilder.control<SubstituteWorekerDto | string | null>(absence?.SubstituteWorker ?? null, [Validators.required, autocompleteValidator]),
    });
  }

  private buildAbsenceFormGroup(absence: AbsenceDto) {
    this.canEditAbsenceForm$.next(!this.data.Absence || this.data.Absence.StatusId === AbsenceStatus.Draft);
    this.absenceForm = this.createAbsenceFormGroup(absence);

    setFormControlValidators(this.absenceForm, [
      this.workingTimePeriodValidator,
      maxDateRangeValidator(
        this.startDate,
        this.endDate,
        this.absenceDateRange$.value,
        this.areWeekendsDaysOff$.getValue(),
        this.holidayCalendar$.getValue().map((d) => d.Date),
        this.absenceLimitCalcType$.getValue(),
        this.ignoreCalendarValidation$.getValue(),
      ),
    ]);

    if (!this.canEditAbsenceForm$.getValue()) {
      this.absenceForm.disable();

      if (
        this.authService.authServerUserId !== this.data.Absence.AuthServerUserId &&
        this.data.Absence.StatusId === AbsenceStatus.PendingApproval &&
        this.data.Absence.ApprovalPermissions?.some(
          (permissionDto) => permissionDto.CanEditLeaveSubstitutions && this.authService.hasPermission(permissionDto.Permission as Permission),
        )
      ) {
        this.substitutionWorkerEdit$.next(true);
        this.substituteWorkerControl.enable();
      }
    }
  }

  private patchFormGroupValue() {
    const getTimeString = (date: Date | string) =>
      `${new Date(date).getHours().toString().padStart(2, '0')}:${new Date(date).getMinutes().toString().padStart(2, '0')}`;

    if (this.data.Absence) {
      this.absenceForm.patchValue({
        absenceTypeId: this.data.Absence.AbsenceTypeId,
        absenceSubtypeId: this.data.Absence.AbsenceSubtypeId,
        startDate: moment(this.data.Absence.StartDate),
        endDate: moment(this.data.Absence.EndDate),
        startTime: getTimeString(this.data.Absence.StartDate),
        endTime: getTimeString(this.data.Absence.EndDate),
        absenceReason: this.data.Absence.AbsenceReason,
        personToCareFullName: this.data.Absence.PersonToCareFullName,
        personToCareAddress: this.data.Absence.PersonToCareAddress,
        reasonToCare: this.data.Absence.ReasonToCare,
        absenceLeaveCareKinshipDegreeId: this.data.Absence.AbsenceLeaveCareKinshipDegreeId,
        substituteWorker: this.data.Absence.SubstituteWorker,
      });

      this.shouldResetEndDate = false;
    }
  }

  private handleAbsenceTypeChange = () =>
  (this.absenceSubtypes$ = this.absenceForm.get('absenceTypeId').valueChanges.pipe(
    tap(() => {
      this.startDate.reset(null);
      this.endDate.reset(null);
    }),
    switchMap((absenceTypeId) =>
      this.workerAgreementService.getAbsenceSubtypesByWorkerAgreementId(absenceTypeId, this.data.WorkerAgreementId, this.data.Absence?.Id).pipe(
        tap((items: AbsenceSubtypeDto[]) => {
          if (items.length === 1) {
            this.absenceSubtypeIdControl.setValue(items[0].Id);
          }
        }),
      ),
    ),
    takeUntil(this.unsubscribe$),
    shareReplay({ bufferSize: 1, refCount: true }),
  ));

  private handleAbsenceSubtypeChange = () => {
    return combineLatest([
      this.absenceSubtypes$,
      this.absenceSubtypeIdControl.valueChanges.pipe(
        startWith(this.absenceSubtypeIdControl.value),
        distinctUntilChanged()
      )
    ])
      .pipe(
        map(([absenceSubtypes, id]) => absenceSubtypes?.find(as => as.Id === id) ?? null),
        filter((as): as is AbsenceSubtypeDto => !!as),
        distinctUntilChanged((prev, curr) => prev.Id === curr.Id),
        tap((as) => {
          this.absenceSubtype = as;
          this.ignoreCalendarValidation$.next(as.IgnoreCalendarValidation ?? false);
          this.isWorkerFamilyMemberRequired$.next(as.IsWorkerFamilyMemberRequired ?? false);
          this.mayBeHourly$.next(as.MayBeHourly ?? false);
          this.isPaid$.next(as.IsPaid ?? false);
          this.isApprovalRequired = as.ApprovalRequired ?? false;
          this.dateLimits = as.AbsenceLimitLeftHistory;
          this.absenceLimitCalcType$.next(as.AbsenceLimitCalcTypeId);

          this.refreshDateLimits();
          this.setAbsenceTimeRange(as);
          this.requiredFileTypes$.next(as.RequiredAbsenceFileTypes ?? []);

          if (as.Id === AbsenceSubtype.FamilyMemberCare) {
            this.handleFamilyMemberCareAbsence();
          } else if (as.Id === AbsenceSubtype.HouseholdMemberCare) {
            this.handleHouseholdMemberCareAbsence();
          } else {
            this.removeUnpaidCareLeaveValidators();
          }

          if (this.shouldResetEndDate) {
            this.endDate.reset(null);
            this.shouldResetEndDate = false;
          }

          if (as.RequireLeaveSubstitution && this.absenceForm.enabled) {
            this.substituteWorkerControl.enable();
          } else {
            if (!as.RequireLeaveSubstitution) {
              this.substituteWorkerControl.setValue(null, { emitEvent: false });
            }
            this.substituteWorkerControl.disable();
          }

          this.absenceForm.updateValueAndValidity();
          this.changeDetector.markForCheck();
        }),

        switchMap((as) =>
          this.absenceService.getAbsenceApprovalProgress({
            AbsenceId: this.data?.Absence?.Id,
            AbsenceSubtypeId: as.Id,
            WorkerAgreementId: this.data.WorkerAgreementId
          }).pipe(
            map(progress => this.mapProgressToApprovalSteps(progress)),
            catchError(err => {
              return of([] as ApprovalStep[]);
            })
          )
        ),

        takeUntil(this.unsubscribe$)
      )
      .subscribe((steps) => {
        this.intermediateSteps = steps;
        this.changeDetector.markForCheck();
      });
  };

  private setAbsenceDateRange = (absenceSubtype: AbsenceSubtypeDto, selectedDate: Date) => {
    return this.absenceDateRange$.next(
      !absenceSubtype.IsPaid
        ? 365
        : absenceSubtype.MayBeHourly
          ? this.defaultRangeWhenHourlyAbsence
          : Math.max(-1, this.absenceSubtypeLimit(absenceSubtype, selectedDate) - 1),
    );
  };

  private absenceSubtypeLimit = (absenceSubtype: AbsenceSubtypeDto, selectedDate: Date) =>
    Math.min(
      Math.floor((absenceSubtype.MaximumAbsenceTimeInHours ?? 0) / 24),
      this.getScaledDayLimit(
        absenceSubtype.AnnualAbsenceLimits.find((x) => x.Year === selectedDate?.getFullYear())?.AbsenceLimitLeft,
        selectedDate,
        absenceSubtype.AnnualAbsenceLimits.find((x) => x.Year === selectedDate?.getFullYear())?.DayLimitPools,
      ) ?? 0,
    );

  private onStartDateChange() {
    this.startDate.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(() => this.refreshDateLimits());
  }

  private refreshDateLimits(): void {
    const value: Moment | null = this.startDate.value;
    this.workingTimePeriod = this.workingTimePeriods?.IsScaled
      ? (value?.isValid() ? findPeriod(this.workingTimePeriods.Periods, value.toDate()) : this.data.WorkingTimePeriod ?? null)
      : null;
    if (!this.absenceSubtype) {
      this.revalidateDateControls();
      return;
    }
    const selectedDate = value?.isValid() ? value.toDate() : null;
    const year = !this.isTemporaryAgreement() && value?.year() + 1 === new Date().getFullYear()
      ? value.year() + 1 : value?.year();
    const annualLimit = this.absenceSubtype.AnnualAbsenceLimits.find((limit) => limit.Year === year);
    this.overdueLimitLeft$.next(this.getScaledDayLimit(annualLimit?.OverdueLimitLeft, selectedDate, annualLimit?.OverdueDayLimitPools) ?? 0);
    if (value?.isValid() && value.year() === new Date().getFullYear() - 1 && !this.isTemporaryAgreement()) {
      this.absenceDateRange$.next(Math.max(-1, this.overdueLimitLeft$.value - 1));
    } else if (selectedDate && selectedDate < new Date().resetTime() && this.isTemporaryAgreement() && this.hasHistoryLimits()) {
      this.absenceDateRange$.next(Math.max(-1, this.getHistoryLimit(value) - 1));
    } else {
      this.setAbsenceDateRange(this.absenceSubtype, selectedDate);
    }
    this.revalidateDateControls();
  }

  private revalidateDateControls(): void {
    // Material's filter validators read the current filter state; do not emit date changes or reset the range.
    this.startDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.endDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.absenceForm.updateValueAndValidity({ emitEvent: false });
  }

  private setAbsenceTimeRange = (absenceSubtype: AbsenceSubtypeDto) => {
    return this.absenceTimeRange$.next(
      absenceSubtype.MayBeHourly
        ? Math.min(
          absenceSubtype.AnnualAbsenceLimits.find((x) => x.Year === new Date().getFullYear())?.AbsenceLimitLeft,
          absenceSubtype.MaximumAbsenceTimeInHours,
        )
        : 0,
    );
  };

  private handleHouseholdMemberCareAbsence() {
    this.workerService.getWorkerAddress(this.data.WorkerId).pipe(takeUntil(this.unsubscribe$)).subscribe((address) => {
      this.absenceForm.get('personToCareAddress').setValue(address.WorkerAddress);
      this.changeDetector.markForCheck();
    });

    this.personToCareFullName.setValidators([Validators.required, Validators.maxLength(100), Validators.minLength(1)]);
    this.personToCareAddress.setValidators([Validators.required, Validators.maxLength(100), Validators.minLength(1)]);
    this.reasonToCare.setValidators([Validators.required, Validators.maxLength(300), Validators.minLength(1)]);
    this.absenceLeaveCareKinshipDegreeId.setValidators(null);

    this.personToCareFullName.updateValueAndValidity();
    this.personToCareAddress.updateValueAndValidity();
    this.reasonToCare.updateValueAndValidity();
    this.absenceLeaveCareKinshipDegreeId.updateValueAndValidity();
  }

  private handleFamilyMemberCareAbsence() {
    this.absenceLeaveCareKinshipDegrees$ = this.dictionaryService.getAbsenceLeaveCareKinshipDegrees();
    this.personToCareFullName.setValidators([Validators.required, Validators.maxLength(100), Validators.minLength(1)]);
    this.reasonToCare.setValidators([Validators.required, Validators.maxLength(300), Validators.minLength(1)]);
    this.absenceLeaveCareKinshipDegreeId.setValidators([Validators.required]);
    this.personToCareAddress.setValidators(null);

    this.personToCareFullName.updateValueAndValidity();
    this.reasonToCare.updateValueAndValidity();
    this.absenceLeaveCareKinshipDegreeId.updateValueAndValidity();
    this.personToCareAddress.updateValueAndValidity();
  }

  private removeUnpaidCareLeaveValidators() {
    this.personToCareFullName.setValidators(null);
    this.reasonToCare.setValidators(null);
    this.absenceLeaveCareKinshipDegreeId.setValidators(null);
    this.personToCareAddress.setValidators(null);

    this.personToCareFullName.updateValueAndValidity();
    this.reasonToCare.updateValueAndValidity();
    this.absenceLeaveCareKinshipDegreeId.updateValueAndValidity();
    this.personToCareAddress.updateValueAndValidity();
  }

  private handleAbsenceRangeChange() {
    combineLatest([this.absenceDateRange$, this.absenceTimeRange$, this.mayBeHourly$, this.isPaid$, this.areWeekendsDaysOff$, this.holidayCalendar$, this.ignoreCalendarValidation$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([dateRange, timeRange, mayBeHourly, isPaid, areWeekendsDaysOff, daysOff, ignoreCalendarValidation]) => {
        const validators: ValidatorFn[] = [this.workingTimePeriodValidator];
        if (isPaid) {
          validators.push(
            maxDateRangeValidator(
              this.startDate,
              this.endDate,
              dateRange,
              areWeekendsDaysOff,
              daysOff.map((d) => d.Date),
              this.absenceLimitCalcType$.getValue(),
              ignoreCalendarValidation,
            ),
          );
        }
        if (mayBeHourly) {
          validators.push(maxTimeRangeValidator(this.startTime, this.endTime, timeRange));
        }
        setFormControlValidators(this.absenceForm, validators);
        this.revalidateDateControls();
      });
  }

  private getScaledDayLimit(limit: number | null | undefined, selectedDate: Date | null, pools?: AbsenceDayLimitPoolDto[] | null): number | null {
    if (this.absenceSubtype?.MayBeHourly) {
      return limit ?? null;
    }

    const period = selectedDate && this.workingTimePeriods?.IsScaled
      ? findPeriod(this.workingTimePeriods.Periods, selectedDate) : null;
    return scaleDayLimit(limit, pools, period, this.absenceSubtype?.AbsenceLimitScalingRule?.Strategy);
  }

  private areFilesToUploadValid = () =>
    this.isWorkerFamilyMemberRequired$.getValue() ? this.areAnyFamilyMembersAttached() : this.areAllRequiredTypesAttached();

  private areAnyFamilyMembersAttached = () => !!Array.from(this.familyMemberFilesMap.keys()).length;

  private areAllRequiredTypesAttached = () =>
    this.requiredFileTypes$.getValue().every((fileType) =>
      this.absenceFilesToUpload
        .map((f) => f.AbsenceFileTypeId)
        .concat(this.data.Absence?.Files.map((f) => f.AbsenceFileTypeId) ?? [])
        .some((f) => f == fileType.Id),
    );

  private handleMayBeHourlyChange = () => {
    this.mayBeHourly$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((mayBeHourly) => !mayBeHourly && this.startTime?.reset(null)),
        tap((mayBeHourly) => !mayBeHourly && this.endTime?.reset(null)),
        tap((mayBeHourly) => {
          if (mayBeHourly) {
            const startDateControl = this.startDate;
            const endDateControl = this.endDate;
            if (startDateControl && endDateControl && startDateControl.value) {
              endDateControl.setValue(startDateControl.value);
            }
          }
        }),
      )
      .subscribe((mayBeHourly) => {
        setFormControlValidators(this.absenceForm.get('startTime'), mayBeHourly ? [Validators.required] : []);
        setFormControlValidators(this.absenceForm.get('endTime'), mayBeHourly ? [Validators.required] : []);
      });

    if (this.startDate) {
      combineLatest([
        this.startDate.valueChanges.pipe(startWith(this.startDate.value)),
        this.mayBeHourly$
      ])
        .pipe(
          takeUntil(this.unsubscribe$),
          filter(([startDate, mayBeHourly]) => mayBeHourly && !!startDate)
        )
        .subscribe(([startDate, _]) => {
          if (this.endDate) {
            this.endDate.setValue(startDate);
          }
        });
    }
  };

  private isBeforeFirstDayOfPreviousMonth(date: Moment | Date | string | null) {
    if (!this.hasBeforePreviousMonthStartLimit) {
      return false;
    }

    const momentDate = moment.isMoment(date) ? date : moment(date);
    const firstDayOfPreviousMonth = moment().subtract(1, 'month').startOf('month');

    return momentDate.isBefore(firstDayOfPreviousMonth);
  }

  private isAllowToSkipPreviousMonthAbsenceLimit(): boolean {
    return this.authService.hasPermission(Permission.AllowSkippingPreviousMonthAbsenceLimit);
  }

  displayValue(value: SubstituteWorekerDto): string | undefined {
    return value?.FullName;
  }

  mapProgressToApprovalSteps(
    stepsDto: AbsenceApprovalProgressStepDto[]
  ): ApprovalStep[] {
    return (stepsDto ?? []).map((s, idx) => {
      const state = this.mapState(s.StepStatus);
      const tooltip = this.buildTooltip(state, s.Approvals);

      return {
        key: this.makeKey(s.StepName, idx),
        label: s.StepName,
        state,
        tooltip
      };
    });
  }

  mapState(status: StepStatusEnum): StepState {
    switch (status) {
      case StepStatusEnum.Done: return 'done';
      case StepStatusEnum.Rejected: return 'rejected';
      case StepStatusEnum.InactiveWait: return 'inactiveWait';
      default: return 'wait';
    }
  }

  buildTooltip(state: StepState, approvals: AbsenceApprovalDto[] | null | undefined): string | null {
    if (!approvals || approvals.length === 0) {
      return null;
    }

    if (state === 'done') {
      const lastPos = this.findLast(approvals, a => a.IsPositive === true);
      if (lastPos) {
        return this.translateService.instant('AbsenceStatusTooltip.AcceptedBy', {
          userName: lastPos.CreatedBy,
          date: this.fmtDate(lastPos.CreatedOn)
        });
      }
    }

    if (state === 'rejected') {
      const lastNeg = this.findLast(approvals, a => a.IsPositive === false);
      if (lastNeg) {
        return this.translateService.instant('AbsenceStatusTooltip.RejectedBy', {
          userName: lastNeg.CreatedBy,
          date: this.fmtDate(lastNeg.CreatedOn)
        });
      }
    }

    return null;
  }

  fmtDate(d: Date | null): string {
    if (!d) {
      return '';
    }
    return new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  makeKey(name: string, idx: number): string {
    const slug = (name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
    return slug ? `${slug}-${idx}` : `step-${idx}`;
  }

  private findLast<T>(arr: T[], pred: (x: T) => boolean): T | undefined {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (pred(arr[i])) {
        return arr[i];
      }
    }
    return undefined;
  }
}
